import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import {
  createAgentInteraction,
  getAgentClient,
  updateAgentPipelineStatus,
} from "../api/endpoints/agents"
import {
  formatCurrency,
  formatDateTime,
  formatDurationSeconds,
  formatEnumLabel,
} from "../utils/formatting"
import { queueInteractionSubmission } from "../utils/offline_queue"
import { Button, Drawer, PageSection, StatusPill } from "./ui"

const INTERACTION_TYPES = [
  "PHONE_CALL",
  "SMS",
  "WHATSAPP",
  "IN_PERSON",
  "EMAIL",
  "OTHER",
] as const

const OUTCOMES = [
  "REACHED",
  "NOT_REACHED",
  "WRONG_NUMBER",
  "PROMISE_TO_PAY",
  "REFUSED_TO_PAY",
  "DISPUTED",
  "ESCALATED",
  "CALLBACK_REQUESTED",
  "PARTIAL_PAYMENT",
  "FULL_PAYMENT",
  "BROKEN_PROMISE",
] as const

const PIPELINE_STATUSES = ["NEW", "OLD", "BROKEN_PROMISE", "PROMISE_TO_PAY", "DONE"] as const

function toLocalDateTimeInputValue(value?: string | null) {
  if (!value) {
    return ""
  }

  const date = new Date(value)
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return localDate.toISOString().slice(0, 16)
}

function toIsoOrUndefined(value: string) {
  return value ? new Date(value).toISOString() : undefined
}

function formatPhoneForWhatsApp(value?: string | null) {
  return value ? value.replace(/\D/g, "") : ""
}

function pipelineTone(
  status?: string | null,
): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "DONE":
      return "success"
    case "PROMISE_TO_PAY":
      return "info"
    case "BROKEN_PROMISE":
      return "danger"
    case "OLD":
      return "warning"
    default:
      return "neutral"
  }
}

function feedbackTone(message: string) {
  const normalized = message.toLowerCase()
  return (
    normalized.includes("failed") ||
    normalized.includes("required") ||
    normalized.includes("must") ||
    normalized.includes("restricted") ||
    normalized.includes("forbidden")
  )
    ? "bg-red-50 border-red-500 text-red-800"
    : "bg-emerald-50 border-emerald-500 text-emerald-800"
}

export function AgentClientDrawer({
  agentId,
  clientId,
  onClose,
}: {
  agentId: string
  clientId: string | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [interactionType, setInteractionType] = useState<(typeof INTERACTION_TYPES)[number]>("PHONE_CALL")
  const [outcome, setOutcome] = useState<(typeof OUTCOMES)[number]>("REACHED")
  const [notes, setNotes] = useState("")
  const [followUpDate, setFollowUpDate] = useState("")
  const [ptpDate, setPtpDate] = useState("")
  const [statusChangeTo, setStatusChangeTo] = useState("")
  const [contactedNok, setContactedNok] = useState(false)
  const [submissionMessage, setSubmissionMessage] = useState("")
  const [manualStatus, setManualStatus] = useState("")
  const [manualPtpDate, setManualPtpDate] = useState("")
  const [manualReason, setManualReason] = useState("")
  const [statusMessage, setStatusMessage] = useState("")

  const detailQuery = useQuery({
    queryKey: ["agent-client-detail", agentId, clientId],
    queryFn: () => getAgentClient(agentId, clientId!),
    enabled: Boolean(agentId && clientId),
  })

  const detail = detailQuery.data?.data
  const requiresInteractionPtp =
    outcome === "PROMISE_TO_PAY" || statusChangeTo === "PROMISE_TO_PAY"
  const requiresManualPtp = manualStatus === "PROMISE_TO_PAY"
  const primaryPhone = detail?.phoneNumber ?? ""
  const whatsappNumber = formatPhoneForWhatsApp(primaryPhone)

  async function refreshAgentQueries() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["agent-pipeline", agentId] }),
      queryClient.invalidateQueries({ queryKey: ["agent-clients", agentId] }),
      queryClient.invalidateQueries({ queryKey: ["agent-client-detail", agentId, clientId] }),
      queryClient.invalidateQueries({ queryKey: ["agent-interactions", agentId] }),
      queryClient.invalidateQueries({ queryKey: ["agent-tasks", agentId] }),
      queryClient.invalidateQueries({ queryKey: ["agent-performance", agentId] }),
    ])
  }

  const interactionMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => createAgentInteraction(agentId, payload),
    onSuccess: async () => {
      setSubmissionMessage("Interaction logged.")
      setNotes("")
      setFollowUpDate("")
      setPtpDate("")
      setStatusChangeTo("")
      setContactedNok(false)
      await refreshAgentQueries()
    },
    onError: (error) => {
      setSubmissionMessage((error as Error).message)
    },
  })

  const statusMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => updateAgentPipelineStatus(agentId, payload),
    onSuccess: async () => {
      setStatusMessage("Pipeline status updated.")
      await refreshAgentQueries()
    },
    onError: (error) => {
      setStatusMessage((error as Error).message)
    },
  })

  useEffect(() => {
    setInteractionType("PHONE_CALL")
    setOutcome("REACHED")
    setNotes("")
    setFollowUpDate("")
    setPtpDate("")
    setStatusChangeTo("")
    setContactedNok(false)
    setSubmissionMessage("")
    setStatusMessage("")
  }, [clientId])

  useEffect(() => {
    if (!detail) {
      return
    }

    setManualStatus(detail.loanSummary.pipelineStatus ?? "OLD")
    setManualPtpDate(toLocalDateTimeInputValue(detail.loanSummary.ptpDate))
    setManualReason("")
  }, [detail])

  async function handleInteractionSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!detail) {
      return
    }

    if (requiresInteractionPtp && !ptpDate) {
      setSubmissionMessage("PTP date is required when Promise to Pay is selected.")
      return
    }

    const payload = {
      loanId: detail.loanSummary.loanId,
      interactionType,
      outcome,
      notes: notes || undefined,
      followUpDate: toIsoOrUndefined(followUpDate),
      ptpDate: requiresInteractionPtp ? toIsoOrUndefined(ptpDate) : undefined,
      statusChangeTo: statusChangeTo || undefined,
      contactedNok,
    }

    if (!navigator.onLine) {
      queueInteractionSubmission(agentId, payload)
      setSubmissionMessage("You are offline. Interaction queued for sync.")
      return
    }

    try {
      await interactionMutation.mutateAsync(payload)
    } catch {
      // handled in mutation callbacks
    }
  }

  async function handleStatusSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!detail || !manualStatus) {
      return
    }

    if (requiresManualPtp && !manualPtpDate) {
      setStatusMessage("PTP date is required when setting Promise to Pay.")
      return
    }

    if (
      manualStatus === "DONE" &&
      !window.confirm("Mark this case as done? This should only be used for a resolved case.")
    ) {
      return
    }

    try {
      await statusMutation.mutateAsync({
        loanId: detail.loanSummary.loanId,
        toStatus: manualStatus,
        reason: manualReason || undefined,
        ptpDate: requiresManualPtp ? toIsoOrUndefined(manualPtpDate) : undefined,
      })
    } catch {
      // handled in mutation callbacks
    }
  }

  return (
    <Drawer onClose={onClose} open={Boolean(clientId)} title={detail?.fullName ?? "Client action"}>
      {detailQuery.isLoading ? <p>Loading client action view...</p> : null}
      {detailQuery.isError ? (
        <div className="border-l-4 border-red-500 rounded-r bg-red-50 p-3 text-sm text-red-800">
          {(detailQuery.error as Error).message}
        </div>
      ) : null}
      {detail ? (
        <div className="flex flex-col gap-4">
          <PageSection title="Client summary">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1 text-sm text-slate-600">
                <div className="text-lg font-semibold text-slate-900">{detail.fullName}</div>
                <div>{detail.phoneNumber}</div>
                <div>{detail.altPhone ?? "No alternate phone"}</div>
                <div>{detail.loanSummary.loanId}</div>
                <div>{detail.loanSummary.productName}</div>
              </div>
              <div className="space-y-2 text-sm text-slate-600">
                <div className="text-lg font-semibold text-slate-900">
                  {formatCurrency(
                    detail.loanSummary.outstandingBalance,
                    detail.loanSummary.currency,
                  )}
                </div>
                <div>
                  Principal:{" "}
                  {formatCurrency(
                    detail.loanSummary.principalAmount,
                    detail.loanSummary.currency,
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill tone={pipelineTone(detail.loanSummary.pipelineStatus)}>
                    {formatEnumLabel(detail.loanSummary.pipelineStatus)}
                  </StatusPill>
                  <StatusPill tone="warning">
                    {formatEnumLabel(detail.loanSummary.overdueCategory)}
                  </StatusPill>
                  <span>{detail.loanSummary.daysOverdue} days overdue</span>
                </div>
                <div>PTP date: {formatDateTime(detail.loanSummary.ptpDate)}</div>
                <div>
                  Last payment:{" "}
                  {detail.loanSummary.lastPaymentAmount
                    ? formatCurrency(
                        detail.loanSummary.lastPaymentAmount,
                        detail.loanSummary.currency,
                      )
                    : "-"}{" "}
                  on {formatDateTime(detail.loanSummary.lastPaymentAt)}
                </div>
                <div>Last interaction: {formatDateTime(detail.loanSummary.lastContactAt)}</div>
              </div>
            </div>
          </PageSection>

          <PageSection title="Communication actions">
            <div className="flex flex-wrap gap-3">
              <a
                className="inline-flex items-center justify-center rounded-md border border-blue-600 bg-blue-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-blue-700"
                href={primaryPhone ? `tel:${primaryPhone}` : undefined}
              >
                Call client
              </a>
              <a
                className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50"
                href={primaryPhone ? `sms:${primaryPhone}` : undefined}
              >
                Send SMS
              </a>
              <a
                className="inline-flex items-center justify-center rounded-md border border-emerald-600 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700 transition-colors hover:bg-emerald-100"
                href={whatsappNumber ? `https://wa.me/${whatsappNumber}` : undefined}
                rel="noreferrer"
                target="_blank"
              >
                Open WhatsApp
              </a>
            </div>
          </PageSection>

          <PageSection title="Next of kin">
            <p className="mb-3 text-sm text-slate-600">
              {detail.canContactNextOfKin
                ? "Next-of-kin contact is currently allowed."
                : `Contact restricted until ${formatEnumLabel(detail.nextOfKinContactMinCategory)}.`}
            </p>
            <ul className="flex flex-col gap-3 list-none p-0 m-0">
              {detail.nextOfKin.map((entry: any) => (
                <li
                  className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
                  key={`${entry.fullName}-${entry.phoneNumber}`}
                >
                  <div className="font-semibold text-slate-900">{entry.fullName}</div>
                  <div className="text-slate-600">
                    {entry.relationship} · {entry.phoneNumber}
                  </div>
                </li>
              ))}
            </ul>
          </PageSection>

          <PageSection title="Log interaction">
            <form className="grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={handleInteractionSubmit}>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-slate-700">Channel</span>
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  onChange={(event) => setInteractionType(event.target.value as (typeof INTERACTION_TYPES)[number])}
                  value={interactionType}
                >
                  {INTERACTION_TYPES.map((entry) => (
                    <option key={entry} value={entry}>
                      {formatEnumLabel(entry)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-slate-700">Outcome</span>
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  onChange={(event) => setOutcome(event.target.value as (typeof OUTCOMES)[number])}
                  value={outcome}
                >
                  {OUTCOMES.map((entry) => (
                    <option key={entry} value={entry}>
                      {formatEnumLabel(entry)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-slate-700">Status change</span>
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  onChange={(event) => setStatusChangeTo(event.target.value)}
                  value={statusChangeTo}
                >
                  <option value="">Keep current</option>
                  {PIPELINE_STATUSES.map((entry) => (
                    <option key={entry} value={entry}>
                      {formatEnumLabel(entry)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-slate-700">Follow-up date</span>
                <input
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  onChange={(event) => setFollowUpDate(event.target.value)}
                  type="datetime-local"
                  value={followUpDate}
                />
              </label>
              {requiresInteractionPtp ? (
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-slate-700">PTP date</span>
                  <input
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    onChange={(event) => setPtpDate(event.target.value)}
                    type="datetime-local"
                    value={ptpDate}
                  />
                </label>
              ) : null}
              <label className="col-span-full flex items-center gap-2 text-sm text-slate-700">
                <input
                  checked={contactedNok}
                  disabled={!detail.canContactNextOfKin}
                  onChange={(event) => setContactedNok(event.target.checked)}
                  type="checkbox"
                />
                This interaction contacted next of kin
              </label>
              <label className="col-span-full flex flex-col gap-1.5">
                <span className="text-xs font-medium text-slate-700">Notes</span>
                <textarea
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  onChange={(event) => setNotes(event.target.value)}
                  rows={4}
                  value={notes}
                />
              </label>
              <div className="col-span-full">
                <Button disabled={interactionMutation.isPending} type="submit">
                  {interactionMutation.isPending ? "Saving..." : "Log interaction"}
                </Button>
              </div>
            </form>
            {submissionMessage ? (
              <div className={`mt-4 rounded-r border-l-4 p-3 text-sm ${feedbackTone(submissionMessage)}`}>
                {submissionMessage}
              </div>
            ) : null}
          </PageSection>

          <PageSection title="Status change">
            <form className="grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={handleStatusSubmit}>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-slate-700">Pipeline status</span>
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  onChange={(event) => setManualStatus(event.target.value)}
                  value={manualStatus}
                >
                  {PIPELINE_STATUSES.map((entry) => (
                    <option key={entry} value={entry}>
                      {formatEnumLabel(entry)}
                    </option>
                  ))}
                </select>
              </label>
              {requiresManualPtp ? (
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-slate-700">PTP date</span>
                  <input
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    onChange={(event) => setManualPtpDate(event.target.value)}
                    type="datetime-local"
                    value={manualPtpDate}
                  />
                </label>
              ) : null}
              <label className="col-span-full flex flex-col gap-1.5">
                <span className="text-xs font-medium text-slate-700">Reason</span>
                <textarea
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  onChange={(event) => setManualReason(event.target.value)}
                  rows={3}
                  value={manualReason}
                />
              </label>
              <div className="col-span-full">
                <Button disabled={statusMutation.isPending} type="submit" variant="secondary">
                  {statusMutation.isPending ? "Updating..." : "Update status"}
                </Button>
              </div>
            </form>
            {statusMessage ? (
              <div className={`mt-4 rounded-r border-l-4 p-3 text-sm ${feedbackTone(statusMessage)}`}>
                {statusMessage}
              </div>
            ) : null}
          </PageSection>

          <PageSection title="Interaction history">
            <ul className="flex flex-col gap-3 list-none p-0 m-0">
              {detail.interactionHistory.map((entry: any) => (
                <li className="rounded-lg border border-slate-200 p-3" key={entry.interactionId}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-semibold text-slate-900">
                      {formatEnumLabel(entry.interactionType)}
                    </div>
                    <div className="text-xs text-slate-500">{formatDateTime(entry.createdAt)}</div>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                    <StatusPill tone="info">{formatEnumLabel(entry.outcome)}</StatusPill>
                    {entry.toPipelineStatus ? (
                      <span>
                        {formatEnumLabel(entry.fromPipelineStatus)} {"->"}{" "}
                        {formatEnumLabel(entry.toPipelineStatus)}
                      </span>
                    ) : null}
                    <span>Duration: {formatDurationSeconds(entry.durationSeconds)}</span>
                    <span>Logged by: {entry.agentName ?? "-"}</span>
                  </div>
                  {entry.notes ? <p className="mt-2 text-sm text-slate-700">{entry.notes}</p> : null}
                  <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
                    <span>Follow-up: {formatDateTime(entry.followUpDate)}</span>
                    <span>PTP: {formatDateTime(entry.ptpDate)}</span>
                  </div>
                </li>
              ))}
            </ul>
          </PageSection>
        </div>
      ) : null}
    </Drawer>
  )
}
